import RenderComponents from "@utilities/renderComponents";
import slugify from "@utilities/slugify";
import clsx from "clsx";
import IconExternal from "../../public/assets2019/img/svg/external.svg";

import { useRouter } from "next/router";
import { useEffect, useRef, useState } from "react";
import { Tab, TabList, TabPanel, Tabs } from "react-tabs";

import Cta from "@components/Cta/Cta";
import Link from "@components/Link/Link";
import { TypeFacultyProfilePublicationsTabsFields } from "@customTypes/contentful";
import { fetchApi } from "@utilities/fetchApi";
import { useFacultyDetails } from "../../context/facultyDetails";
import styles from "./C14.1_FacultyProfilePublications.module.scss";

export interface TabItem {
  fields: {
    content: any;
  };
}

interface PublicationAuthorFullName {
  suppressPublication: boolean;
  publicationAuthorFullName: {
    facultyProfileData: {
      profileSurname: string;
      profileFirstname: string;
    };
  };
}

interface PublicationAuthorsCollection {
  items: PublicationAuthorFullName[];
}

interface Publication {
  basePageTemplate?: {
    pageTitle: string;
  };
  publicationNote?: string;
  publicationTitle?: string;
  publicationType?: string;
  slug?: string;
  publicationYear?: number;
  publicationAuthorsCollection?: PublicationAuthorsCollection;
  publicationExternalAuthors?: string;
}

interface CustomTabs {
  customFirstTabAnchorToken?: string;
  customFirstTabText?: string;
  customTab1AnchorToken?: string | null;
  customTab1Text?: string | null;
  customTab2AnchorToken?: string | null;
  customTab2Text?: string | null;
  publicationTabs1AnchorToken?: string;
  publicationTabs1Text?: string;
  publicationTabs1TypeList?: string;
  publicationTabs2AnchorToken?: string;
  publicationTabs2Text?: string;
  publicationTabs2TypeList?: string;
  publicationTabs3AnchorToken?: string;
  publicationTabs3Text?: string;
  publicationTabs3TypeList?: string;
}

export const FacultyProfilePublications = (props: {
  content: TypeFacultyProfilePublicationsTabsFields;
}) => {
  const facultyDetails = useFacultyDetails();
  const profileFirstname =
    facultyDetails.facultyProfileData?.fields?.profileFirstname;
  const profileSurname =
    facultyDetails.facultyProfileData?.fields?.profileSurname;

  const { content } = props;

  const [tabIndex, setTabIndex] = useState<number>(0);
  const [toggleIndexes, setToggleIndexes] = useState<number[]>([]);
  const [tabContent, setTabContent] = useState<any>();
  const [didMount, setDidMount] = useState(false);
  const tabsRef = useRef<HTMLElement>(null);
  const [facultyProfilePublicationsData, setFacultyProfilePublicationsData] =
    useState<CustomTabs>({});
  const thirdPublicationRefs = useRef<Record<number, HTMLAnchorElement | null>>(
    {}
  );

  const [
    facultyProfilePublicationsContent,
    setFacultyProfilePublicationsContent,
  ] = useState<Publication[]>([]);
  const { asPath, replace } = useRouter();

  const { awardsAndHonours, researchInterests } = content;

  function filterPublications(publications, type) {
    // Filter based on publication type and suppressPublication authors
    const filteredPublications = publications.filter((publication) => {
      if (publication.publicationType === null) {
        publication.publicationType = "P";
      }
      if (publication && publication.publicationAuthorsCollection) {
        publication.publicationAuthorsCollection.items =
          publication?.publicationAuthorsCollection?.items.filter(
            (author) => !author.suppressPublication
          );
      }

      return publication.publicationType === type;
    });

    return sortPublications(filteredPublications);
  }
  // Sort by year descending and title ascending
  function sortPublications(publications) {
    publications.sort((a, b) => {
      if (a.publicationYear === b.publicationYear) {
        return a.publicationTitle
          ? a.publicationTitle.localeCompare(b.publicationTitle)
          : b.publicationTitle - a.publicationTitle;
      }
      return b.publicationYear - a.publicationYear;
    });
    return publications;
  }

  useEffect(() => {
    if (tabContent && tabContent[0]?.tabAnchorToken) {
      if (!didMount) {
        setDidMount(true);
        const id = /(#)([a-z]+[\w\-.]*)$/gi.exec(asPath);

        if (!id) return;

        const tabIndex = tabContent.findIndex(
          (tab) => slugify(tab.tabAnchorToken) === id![2]
        );

        if (tabIndex === -1) return;

        setTabIndex(tabIndex);

        if (tabsRef) tabsRef.current?.scrollIntoView();
        return;
      }

      replace({
        hash: slugify(tabContent[tabIndex].tabAnchorToken),
      });
    }
  }, [tabContent, tabIndex]);

  useEffect(() => {
    const getData = async () => {
      const data = await fetchApi("/api/getFacultyProfilePublicationData", {
        profileFirstname: profileFirstname,
        profileSurname: profileSurname,
      });
      const { tabsData, contentData } = data;
      setFacultyProfilePublicationsData(tabsData);
      if (contentData?.items?.[0]) {
        setFacultyProfilePublicationsContent(
          contentData?.items[0]?.linkedFrom?.facultyProfileDetailCollection
            ?.items[0]?.linkedFrom?.publicationAuthorCollection?.items[0]
            ?.linkedFrom?.publicationDetailCollection?.items
        );
      }
    };
    getData();
  }, []);

  useEffect(() => {
    const tabContentStructure = [
      ...(researchInterests
        ? [
            {
              tabText: facultyProfilePublicationsData?.customFirstTabText,
              tabAnchorToken:
                facultyProfilePublicationsData?.customFirstTabAnchorToken,
              tabContent: researchInterests,
              isPublications: false,
            },
          ]
        : []),
      ...(facultyProfilePublicationsContent?.length > 0 &&
      filterPublications(facultyProfilePublicationsContent, "P")?.length > 0
        ? [
            {
              tabText: facultyProfilePublicationsData?.publicationTabs1Text,
              tabAnchorToken:
                facultyProfilePublicationsData?.publicationTabs1AnchorToken,
              tabContent: filterPublications(
                facultyProfilePublicationsContent,
                "P"
              ),
              isPublications: true,
            },
          ]
        : []),
      ...(facultyProfilePublicationsContent?.length > 0 &&
      filterPublications(facultyProfilePublicationsContent, "W")?.length > 0
        ? [
            {
              tabText: facultyProfilePublicationsData?.publicationTabs2Text,
              tabAnchorToken:
                facultyProfilePublicationsData?.publicationTabs2AnchorToken,
              tabContent: filterPublications(
                facultyProfilePublicationsContent,
                "W"
              ),
              isPublications: true,
            },
          ]
        : []),
      ...(facultyProfilePublicationsContent?.length > 0 &&
      (filterPublications(facultyProfilePublicationsContent, "C")?.length > 0 ||
        filterPublications(facultyProfilePublicationsContent, "B")?.length > 0)
        ? [
            {
              tabText: facultyProfilePublicationsData?.publicationTabs3Text,
              tabAnchorToken:
                facultyProfilePublicationsData?.publicationTabs3AnchorToken,
              tabContent: sortPublications(
                filterPublications(
                  facultyProfilePublicationsContent,
                  "C"
                )?.concat(
                  filterPublications(facultyProfilePublicationsContent, "B")
                )
              ),
              isPublications: true,
            },
          ]
        : []),
      ...(awardsAndHonours
        ? [
            {
              tabText: facultyProfilePublicationsData?.customTab1Text,
              tabAnchorToken:
                facultyProfilePublicationsData?.customTab1AnchorToken,
              tabContent: awardsAndHonours,
              isPublications: false,
            },
          ]
        : []),
    ];

    setTabContent(tabContentStructure);
  }, [facultyProfilePublicationsData, facultyProfilePublicationsContent]);

  const handleOnToggleClick = () => {
    setToggleIndexes([...toggleIndexes, tabIndex]);
  };

  useEffect(() => {
    if (toggleIndexes.includes(tabIndex)) {
      const ref = thirdPublicationRefs.current[tabIndex];
      if (ref) {
        ref.focus();
      }
    }
  }, [toggleIndexes, tabIndex]);

  const displayReadMore = (item, index: number) => {
    return (
      item.isPublications &&
      item.tabContent.length > 2 &&
      !toggleIndexes.includes(index)
    );
  };

  const getVisibleTabContent = (item, index: number) =>
    displayReadMore(item, index)
      ? item.tabContent.slice(0, 2)
      : item.tabContent;

  const tabs = tabContent ?? [];
  const hasTabs = tabs.length > 0;

  return (
    <div
      className={clsx(
        "component",
        styles["programme-benefits"],
        styles["publication-listing"],
        styles.blue,
        !hasTabs && styles.noContent
      )}
    >
      <div className="wrapper">
        {hasTabs && (
          <section ref={tabsRef} className={clsx(styles["tabs"], "cf")}>
            <Tabs
              selectedIndex={tabIndex}
              onSelect={(index) => setTabIndex(index)}
            >
              <TabList>
                {tabs.map(({ tabText }, index) => (
                  <Tab key={`${tabText}${index}`}>{tabText} </Tab>
                ))}
              </TabList>
              {tabs.map((item, index) => {
                const visible = getVisibleTabContent(item, index);
                const showMore = displayReadMore(item, index);
                return (
                  <TabPanel key={`tab-content${index}`}>
                    <div
                      className={clsx(
                        "component",
                        styles["search-results"],
                        styles["publication-listing"],
                        toggleIndexes.includes(tabIndex) &&
                          styles["display-all"]
                      )}
                    >
                      {visible.map((content, index) => {
                        if (item.isPublications) {
                          return (
                            <div
                              key={`${content.publicationTitle}-${index}`}
                              className={clsx(
                                "component",
                                styles.results,
                                styles.row
                              )}
                            >
                              {content.publicationYear && (
                                <p className={styles["result-copy-a"]}>
                                  {content.publicationYear}
                                </p>
                              )}
                              <h4 className={styles["result-heading"]}>
                                {content?.publicationNotes &&
                                content?.publicationNotes.startsWith("url=") ? (
                                  <a
                                    target={"_blank"}
                                    href={
                                      content.publicationNotes.match(
                                        /\[(.*?)\]/
                                      )[1]
                                    }
                                    rel="noreferrer"
                                    ref={
                                      index === 2
                                        ? (el) => {
                                            thirdPublicationRefs.current[
                                              tabIndex
                                            ] = el;
                                          }
                                        : undefined
                                    }
                                  >
                                    <i className={styles["icon-external"]}>
                                      <IconExternal />
                                    </i>
                                    {content.basePageTemplate?.pageTitle ||
                                      content.publicationTitle}
                                  </a>
                                ) : (
                                  <Link
                                    href={content.slug}
                                    ref={
                                      index === 2
                                        ? (el) => {
                                            thirdPublicationRefs.current[
                                              tabIndex
                                            ] = el;
                                          }
                                        : undefined
                                    }
                                  >
                                    {content.basePageTemplate?.pageTitle ||
                                      content.publicationTitle}
                                  </Link>
                                )}
                              </h4>
                              <p className={styles["result-copy-a"]}>
                                {content.publicationExternalAuthors}
                              </p>
                              {content.publicationPublishingDetails && (
                                <p className={styles["result-copy-a"]}>
                                  {content.publicationPublishingDetails}
                                </p>
                              )}
                            </div>
                          );
                        } else {
                          return RenderComponents([content]);
                        }
                      })}
                    </div>
                    {showMore && (
                      <Cta
                        onClick={handleOnToggleClick}
                        url=""
                        customClass="see-more-toggle"
                        light
                        linkText="See More"
                        icon="icon-chevron"
                        type="tertiary"
                        renderAs="button"
                        ariaLabel="See more publications"
                      />
                    )}
                  </TabPanel>
                );
              })}
            </Tabs>
          </section>
        )}
      </div>
    </div>
  );
};

export default FacultyProfilePublications;
